import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SettingsService, ArticleCreatorSettings } from '@/services/settingsService';
import { FeedTokenService, FeedToken } from '@/services/feedTokenService';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { ApiKeyManagement } from '@/components/settings/ApiKeyManagement';
import { TemplateEditor, TemplatePreviewData, ClipPreviewData } from '@/components/TemplateEditor';
import { CompanyPicker } from 'shell/CompanyPicker';
import {
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Check,
  Key,
  Rss,
  PlugZap,
  FileText,
  Newspaper,
  Copy,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { api } from '@/services/api';

type OutputFormatType = 'html' | 'markdown' | 'plain';

const OUTPUT_FORMATS: { value: OutputFormatType; label: string }[] = [
  { value: 'html', label: 'HTML' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'plain', label: 'Plain Text' },
];

interface UserProfile {
  id: string;
  companyId?: string;
}

export const Settings: React.FC = () => {
  const [settings, setSettings] = useState<ArticleCreatorSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('connections');

  // Company selection state
  const { userCompanies, getCompanyColor } = useUserCompanies();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(() => {
    return localStorage.getItem('articleCreator_selectedCompanyId');
  });

  // Form state
  const [outputFormat, setOutputFormat] = useState<OutputFormatType>('html');
  const [episodeTemplate, setEpisodeTemplate] = useState('');
  const [clipTemplate, setClipTemplate] = useState('');

  // Feed token state
  const [feedTokens, setFeedTokens] = useState<FeedToken[]>([]);
  const [feedTokenLoading, setFeedTokenLoading] = useState(false);
  const [feedTokenError, setFeedTokenError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);

  // Template preview data state
  const [previewData, setPreviewData] = useState<TemplatePreviewData | null>(null);


  // Get the active company ID
  const activeCompanyId = selectedCompanyId || companyId;

  // Fetch user's default company on mount
  useEffect(() => {
    const fetchUserCompany = async () => {
      try {
        const response = await api.get<UserProfile>('/users/me');
        if (response.success && response.data?.companyId) {
          setCompanyId(response.data.companyId);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };
    fetchUserCompany();
  }, []);

  // Load settings when company changes
  useEffect(() => {
    if (activeCompanyId) {
      loadSettings(activeCompanyId);
    }
  }, [activeCompanyId]);

  const loadSettings = async (targetCompanyId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await SettingsService.getSettings(targetCompanyId);
      setSettings(data);

      // Populate form with existing settings
      setOutputFormat(data.outputFormat || 'html');
      setEpisodeTemplate(data.episodeTemplate || '');
      setClipTemplate(data.clipTemplate || '');

      // Load feed tokens and preview data in parallel
      await Promise.all([
        loadFeedTokens(targetCompanyId),
        loadPreviewData(targetCompanyId),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadFeedTokens = async (targetCompanyId: string) => {
    try {
      setFeedTokenLoading(true);
      setFeedTokenError(null);
      const tokens = await FeedTokenService.getTokens(targetCompanyId);
      setFeedTokens(tokens);
    } catch (err) {
      setFeedTokenError(err instanceof Error ? err.message : 'Failed to load feed tokens');
    } finally {
      setFeedTokenLoading(false);
    }
  };

  const loadPreviewData = async (targetCompanyId: string) => {
    try {
      const data = await SettingsService.getTemplatePreviewData(targetCompanyId);
      // Transform API response to match TemplatePreviewData interface
      const clips: ClipPreviewData[] = (data.clips || []).map((clip: any) => ({
        clip_title: clip.articleTitle || clip.clip_title || '',
        clip_description: clip.articleDescription || clip.clip_description || '',
        clip_url: clip.articleUrl || clip.clip_url || '',
        clip_image: clip.articleImage || clip.clip_image || '',
      }));

      setPreviewData({
        hasData: data.hasData,
        episode_title: data.episodeTitle,
        episode_description: data.episodeDescription,
        episode_audio_url: data.episodeAudioUrl || '',
        episode_artwork: data.episodeArtwork || '',
        episode_duration: data.episodeDuration || '',
        podbean_episode_id: data.podbeanEpisodeId || '',
        clips,
      });
    } catch (err) {
      console.error('Failed to load preview data:', err);
      // Preview is optional, don't show error to user
    }
  };

  const handleCreateFeedToken = async () => {
    if (!activeCompanyId) return;
    try {
      setFeedTokenLoading(true);
      setFeedTokenError(null);
      await FeedTokenService.createToken(activeCompanyId);
      await loadFeedTokens(activeCompanyId);
    } catch (err) {
      setFeedTokenError(err instanceof Error ? err.message : 'Failed to create feed token');
    } finally {
      setFeedTokenLoading(false);
    }
  };

  const handleRegenerateFeedToken = async () => {
    if (!activeCompanyId || feedTokens.length === 0) return;
    try {
      setRegenerating(true);
      setFeedTokenError(null);
      await FeedTokenService.regenerateToken(feedTokens[0].token, activeCompanyId);
      await loadFeedTokens(activeCompanyId);
      setShowRegenerateWarning(false);
    } catch (err) {
      setFeedTokenError(err instanceof Error ? err.message : 'Failed to regenerate feed token');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyFeedUrl = async () => {
    if (feedTokens.length === 0) return;
    try {
      await navigator.clipboard.writeText(feedTokens[0].feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSave = async () => {
    if (!activeCompanyId) {
      setError('No company selected');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const updatedSettings = await SettingsService.updateSettings({
        outputFormat,
        episodeTemplate,
        clipTemplate,
      }, activeCompanyId);

      // Update local state with response (includes generated rssFeedUrl)
      setSettings(updatedSettings);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="max-w-content px-8 py-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-content px-8 py-8 space-y-6">
      {/* Header with company selector */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          {userCompanies.length > 1 ? (
            <div className="mt-2">
              <CompanyPicker
                companies={userCompanies}
                selectedCompanyId={activeCompanyId || undefined}
                onCompanyChange={(companyId) => {
                  setSelectedCompanyId(companyId);
                  localStorage.setItem('articleCreator_selectedCompanyId', companyId);
                }}
                getCompanyColor={getCompanyColor}
                placeholder="Select company..."
              />
            </div>
          ) : (
            <p className="text-muted-foreground">
              Configure Article Creator for your organization
            </p>
          )}
        </div>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/10 text-green-600 rounded-lg">
          <CheckCircle className="h-5 w-5" />
          <span>Settings saved successfully</span>
        </div>
      )}

      {/* Tabbed Settings */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="connections" className="flex items-center gap-2">
            <PlugZap className="h-4 w-4" />
            Connections
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <Newspaper className="h-4 w-4" />
            Content
          </TabsTrigger>
        </TabsList>

        {/* CONNECTIONS TAB */}
        <TabsContent value="connections" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PlugZap className="h-5 w-5" />
                External Connections
              </CardTitle>
              <CardDescription>
                Configure RSS feed publishing and API access.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible defaultValue="rss" className="w-full">
                {/* RSS Feed Section */}
                <AccordionItem value="rss">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Rss className="h-4 w-4" />
                      <span>RSS Feed</span>
                      {feedTokens.length > 0 && (
                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 space-y-4">
                    {feedTokenError && (
                      <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>{feedTokenError}</span>
                      </div>
                    )}

                    {feedTokenLoading ? (
                      <div className="flex items-center gap-2 py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading...</span>
                      </div>
                    ) : feedTokens.length === 0 ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Create a feed URL to make your articles available via RSS.
                        </p>
                        <Button onClick={handleCreateFeedToken} variant="outline">
                          <Rss className="h-4 w-4 mr-2" />
                          Create Feed URL
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Feed URL</label>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-sm font-mono bg-muted px-3 py-2 rounded-md break-all">
                              {feedTokens[0].feedUrl}
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCopyFeedUrl}
                              className="shrink-0"
                            >
                              {copied ? (
                                <Check className="h-4 w-4 text-green-600" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Share this URL with RSS readers to subscribe to your article feed.
                          </p>
                        </div>

                        {showRegenerateWarning ? (
                          <div className="space-y-3 p-3 border border-amber-200 bg-amber-50 rounded-md">
                            <div className="flex items-start gap-2 text-amber-800">
                              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                              <div className="text-sm">
                                <p className="font-medium">Regenerating will invalidate the current URL.</p>
                                <p className="text-amber-700">Any RSS readers using the old URL will stop receiving updates.</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleRegenerateFeedToken}
                                disabled={regenerating}
                              >
                                {regenerating ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : (
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                )}
                                Confirm Regenerate
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowRegenerateWarning(false)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowRegenerateWarning(true)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Regenerate URL
                          </Button>
                        )}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* API Keys Section */}
                <AccordionItem value="api-keys">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      <span>API Keys</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <ApiKeyManagement companyId={activeCompanyId} />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTENT TAB */}
        <TabsContent value="content" className="space-y-6">
          <Card>
            {/* <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Newspaper className="h-5 w-5" />
                Content Settings
              </CardTitle>
              <CardDescription>
                Configure article format and templates.
              </CardDescription>
            </CardHeader> */}
            <CardContent>
              {/* Article Format - inline row */}
              <div className="flex items-center justify-between py-4 border-b">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span className="font-medium">Article Format</span>
                </div>
                <Select value={outputFormat} onValueChange={(v) => setOutputFormat(v as OutputFormatType)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    {OUTPUT_FORMATS.map((format) => (
                      <SelectItem key={format.value} value={format.value}>
                        {format.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Accordion type="single" collapsible defaultValue="templates" className="w-full">
                {/* Templates Section */}
                <AccordionItem value="templates">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>Content Templates</span>
                      {(episodeTemplate || clipTemplate) && (
                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Configured
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 space-y-4">
                    <TemplateEditor
                      key={`${activeCompanyId}-${previewData?.hasData}`}
                      episodeTemplate={episodeTemplate}
                      clipTemplate={clipTemplate}
                      onEpisodeTemplateChange={setEpisodeTemplate}
                      onClipTemplateChange={setClipTemplate}
                      previewData={previewData}
                    />
                    <p className="text-sm text-muted-foreground">
                      Templates define how your podcast content appears in the RSS feed.
                      The Episode Template wraps each episode, and the Clip Template repeats for each article clip.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
